import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import api from '../utils/api';

type ColumnKind = 'polity' | 'region' | 'civilization';

type Qid = `Q${number}`;

type ChronicleMatrix = {
  meta: {
    generatedAt: string;
    cache: { hit: boolean; key: string };
    params: any;
  };
  columns: Array<{ qid: Qid; label: string; kind: ColumnKind }>;
  buckets: Array<{
    key: string;
    startYear: number;
    endYear: number;
    cells: Record<string, Qid[]>;
  }>;
  events: Record<string, { id: Qid; label: string; startYear: number; wikidataUrl: string }>;
};

const Wrap = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.2rem 1.4rem;
`;

const Title = styled.h1`
  margin: 0 0 0.8rem;
  font-size: 1.5rem;
`;

const Controls = styled.div`
  display: grid;
  grid-template-columns: 140px 140px 140px 160px 1fr auto;
  gap: 0.6rem;
  align-items: end;
  margin-bottom: 1rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.textSecondary};

  input,
  select {
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.text};
    border-radius: 10px;
    padding: 0.55rem 0.6rem;
    outline: none;
  }
`;

const Btn = styled.button`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border-radius: 10px;
  padding: 0.62rem 0.9rem;
  cursor: pointer;
  font-weight: 800;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Hint = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.9rem;
  margin: 0.3rem 0 0.9rem;
`;

const Grid = styled.div`
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 960px;
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.surface};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  padding: 0.6rem 0.7rem;
  text-align: left;
  font-weight: 900;
  white-space: nowrap;
`;

const Td = styled.td`
  border-top: 1px solid ${({ theme }) => theme.border};
  padding: 0.55rem 0.7rem;
  vertical-align: top;
`;

const CellEvent = styled.a`
  display: block;
  color: ${({ theme }) => theme.text};
  text-decoration: none;
  line-height: 1.35;
  margin: 0.15rem 0;

  &:hover {
    color: ${({ theme }) => theme.primary};
    text-decoration: underline;
  }
`;

const ErrorBox = styled.pre`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};
  padding: 0.8rem;
  border-radius: 12px;
  overflow: auto;
`;

const Raw = styled.pre`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  padding: 0.8rem;
  border-radius: 12px;
  overflow: auto;
  margin-top: 1rem;
`;

const Chronicle: React.FC = () => {
  const [startYear, setStartYear] = useState('1900');
  const [endYear, setEndYear] = useState('2000');
  const [bucketSize, setBucketSize] = useState('50');
  const [columnKind, setColumnKind] = useState<ColumnKind>('polity');
  const [columns, setColumns] = useState('Q148,Q142,Q145,Q38');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<ChronicleMatrix | null>(null);

  const columnOrder = useMemo(() => data?.columns ?? [], [data?.columns]);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await api.get<ChronicleMatrix>('/chronicle/matrix', {
        // 该接口首请求可能需要访问 WDQS（Wikidata），5s 默认超时容易触发；这里单独放宽。
        timeout: 30000,
        params: {
          startYear,
          endYear,
          bucketSize,
          columnKind,
          columns,
          // 默认别太大，避免首屏被 WDQS/网络拖垮；需要更多数据再调。
          limit: 120,
        },
      });
      setData(res.data);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        '请求失败（可能是后端未启动，或 WDQS 访问受限/超时）。';
      setErr(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrap>
      <Title>World Chronicle Matrix（Demo）</Title>
      <Controls>
        <Field>
          <span>startYear</span>
          <input value={startYear} onChange={(e) => setStartYear(e.target.value)} />
        </Field>
        <Field>
          <span>endYear</span>
          <input value={endYear} onChange={(e) => setEndYear(e.target.value)} />
        </Field>
        <Field>
          <span>bucketSize</span>
          <input value={bucketSize} onChange={(e) => setBucketSize(e.target.value)} />
        </Field>
        <Field>
          <span>columnKind</span>
          <select value={columnKind} onChange={(e) => setColumnKind(e.target.value as any)}>
            <option value="polity">polity</option>
            <option value="region">region</option>
            <option value="civilization">civilization（需映射表）</option>
          </select>
        </Field>
        <Field>
          <span>columns（逗号分隔 QID，可留空自动选 Top8）</span>
          <input value={columns} onChange={(e) => setColumns(e.target.value)} />
        </Field>
        <Btn onClick={load} disabled={loading}>
          {loading ? '加载中…' : '加载'}
        </Btn>
      </Controls>

      <Hint>
        接口：<code>/api/chronicle/matrix</code>（通过前端 <code>/api</code> 代理访问）。缓存命中：{data ? String(data.meta.cache.hit) : '-'}
      </Hint>

      {err ? <ErrorBox>{err}</ErrorBox> : null}

      {data ? (
        <>
          <Grid>
            <Table>
              <thead>
                <tr>
                  <Th style={{ minWidth: 120 }}>时间</Th>
                  {columnOrder.map((c) => (
                    <Th key={c.qid} style={{ minWidth: 220 }}>
                      {c.label} ({c.qid})
                    </Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.buckets.map((b) => (
                  <tr key={b.key}>
                    <Td>
                      <strong>{b.key}</strong>
                    </Td>
                    {columnOrder.map((c) => {
                      const ids = b.cells?.[c.qid] ?? [];
                      const events = ids
                        .map((id) => data.events?.[id])
                        .filter(Boolean)
                        .slice(0, 8);
                      return (
                        <Td key={c.qid}>
                          {events.length === 0 ? (
                            <span style={{ color: '#888' }}>-</span>
                          ) : (
                            events.map((ev) => (
                              <CellEvent key={ev.id} href={ev.wikidataUrl} target="_blank" rel="noreferrer">
                                {ev.startYear} · {ev.label}
                              </CellEvent>
                            ))
                          )}
                          {ids.length > 8 ? (
                            <div style={{ color: '#888', marginTop: '0.35rem' }}>… 共 {ids.length} 条</div>
                          ) : null}
                        </Td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Grid>

          <Raw>{JSON.stringify(data, null, 2)}</Raw>
        </>
      ) : null}
    </Wrap>
  );
};

export default Chronicle;

